import { Timeouts } from '@gap/utils/date-utils'
import { makeAutoObservable } from 'mobx'

/** How long a WebSocket may stay down before it counts as the server being unreachable. */
export const WEBSOCKET_GRACE_MS = 10 * Timeouts.second

/** How often the server is pinged while HTTP calls are failing to reach it. */
export const HTTP_PROBE_INTERVAL_MS = 10 * Timeouts.second

type ServerReachabilityStoreParams = {
   /** Sends a request that resolves once the server has answered. */
   probe: () => Promise<unknown>
}

/** Records whether the server can be reached, from HTTP calls and from WebSocket connections. */
export class ServerReachabilityStore {
   sp: ServerReachabilityStoreParams
   httpUnreachableSince: number | null = null
   /** When each counted WebSocket dropped, once it has stayed down past the grace period. */
   webSocketUnreachableSince = new Map<object, number>()

   private probeTimer: ReturnType<typeof setInterval> | null = null
   private readonly webSocketGraceTimers = new Map<object, ReturnType<typeof setTimeout>>()

   constructor(sp: ServerReachabilityStoreParams) {
      this.sp = sp
      makeAutoObservable<ServerReachabilityStore, 'probeTimer' | 'webSocketGraceTimers'>(this, {
         sp: false,
         probeTimer: false,
         webSocketGraceTimers: false,
      }, { autoBind: true })
      globalThis.window?.addEventListener('online', this.probeIfUnreachable)
      globalThis.document?.addEventListener('visibilitychange', this.onVisibilityChange)
   }

   /** When the earliest signal still active began, or null while the server is reachable. */
   get unreachableSince(): number | null {
      const activeSignals = [this.httpUnreachableSince, ...this.webSocketUnreachableSince.values()]
         .filter((since): since is number => since !== null)
      return activeSignals.length > 0 ? Math.min(...activeSignals) : null
   }

   /** Records an HTTP call that could not reach the server. */
   reportHttpFailure() {
      if (this.httpUnreachableSince !== null) return
      this.httpUnreachableSince = Date.now()
      this.probeTimer = setInterval(this.probe, HTTP_PROBE_INTERVAL_MS)
   }

   /** Records an HTTP call that the server answered. */
   reportHttpSuccess() {
      if (this.probeTimer) clearInterval(this.probeTimer)
      this.probeTimer = null
      this.httpUnreachableSince = null
   }

   /**
    * Records a WebSocket closing unexpectedly, counted once it stays down past the grace period.
    * @param socket Identifies the connection, so several can be tracked at once.
    */
   reportWebSocketDisconnected(socket: object) {
      if (this.webSocketGraceTimers.has(socket) || this.webSocketUnreachableSince.has(socket)) return
      const disconnectedAt = Date.now()
      this.webSocketGraceTimers.set(socket, setTimeout(() => this.countWebSocketDrop({ socket, disconnectedAt }), WEBSOCKET_GRACE_MS))
   }

   /**
    * Records a WebSocket opening.
    * @param socket The connection that opened.
    */
   reportWebSocketConnected(socket: object) {
      this.forgetWebSocket(socket)
   }

   /**
    * Stops tracking a WebSocket, such as one closed on purpose.
    * @param socket The connection to stop tracking.
    */
   forgetWebSocket(socket: object) {
      const graceTimer = this.webSocketGraceTimers.get(socket)
      if (graceTimer) clearTimeout(graceTimer)
      this.webSocketGraceTimers.delete(socket)
      this.webSocketUnreachableSince.delete(socket)
   }

   private countWebSocketDrop(params: { socket: object, disconnectedAt: number }) {
      this.webSocketGraceTimers.delete(params.socket)
      this.webSocketUnreachableSince.set(params.socket, params.disconnectedAt)
   }

   private onVisibilityChange() {
      if (document.visibilityState === 'visible') this.probeIfUnreachable()
   }

   private probeIfUnreachable() {
      if (this.httpUnreachableSince !== null) this.probe()
   }

   private probe() {
      this.sp.probe().then(this.reportHttpSuccess, () => undefined)
   }
}

/** Pings the public API, which answers without a session. */
async function pingServer() {
   const { apiPublicClientSilent } = await import('api/trpc-public/trpc-client')
   return apiPublicClientSilent.ping.query()
}

export const serverReachabilityStore = new ServerReachabilityStore({ probe: pingServer })
